import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import { useReservoirClient, useTokens } from '@reservoir0x/reservoir-kit-ui';
import { serializeError } from 'serialize-error';
import { useGate } from 'statsig-react';
import { type Address, useBalance } from 'wagmi';
import { ContractMethod, shortenHash } from '@soundxyz/utils/const';
import {
  ERROR_TYPE,
  MINTING_ERROR_ACTIONS,
  MINTING_INFO_ACTIONS,
  PILLARS,
} from '@soundxyz/utils/constants/logging';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ParamsStore } from '@context/params';
import { ToastContext } from '@context/toast';
import { Transaction } from '@context/transaction';
import { trackClickEvent } from '@lib/analytics';
import { FEATURE_GATES, SPACE } from '@utils/constants';
import { formatWeiToEthDisplayFree } from '@utils/helpers';
import { assertIsHex } from '@utils/hex';
import { logError } from './useLogErrorNew';
import { logInfo } from './useLogInfo';
import { useTransactionFlow } from './useTransactionFlow';

export type MarketPlaceCollectionType =
  | 'V1 Edition'
  | 'Artist Contract'
  | 'Album Edition'
  | 'Tiered Edition';

export const useMarketPlace = ({
  quantity,
  releaseId,
  contractAddress,
  contractChainId,
  releaseTitle,
  collectionType,
  marketplaceUrl,
}: {
  releaseId: string;
  contractAddress: string;
  contractChainId: number;
  quantity: number;
  releaseTitle: string;
  collectionType: MarketPlaceCollectionType;
  marketplaceUrl: string | null;
}) => {
  const { authUserAddress } = useAuth();
  const { value: marketPlaceEnabled } = useGate(FEATURE_GATES.BUY_ON_SOUND_MARKET);
  const giftTo = ParamsStore.gift_to;

  const { getSigner, isSwitchingNetwork, getDeepLink } = useConnectedAuth();
  const [isCollecting, setIsCollecting] = useState(false);
  const [currentTxHash, setCurrentTxHash] = useState<string | null>(null);
  const { openToast } = ToastContext.useContainer();
  const client = useReservoirClient();

  const { data: ethBalance } = useBalance({
    address: authUserAddress,
    enabled: !!authUserAddress,
    formatUnits: 'wei',
    chainId: contractChainId,
  });

  const { safeHandleTransaction } = Transaction.useContainer();

  const attributesToFilter = useMemo(() => {
    switch (collectionType) {
      case 'Artist Contract':
        return {
          [`attributes[${releaseTitle}]`]: ['Song Edition', 'Golden Egg'],
        };
      case 'Album Edition':
        return { [`attributes[SINGLE]`]: releaseTitle };
      case 'Tiered Edition':
        return { [`attributes[Tier]`]: ['Limited'] };
      default:
        return {};
    }
  }, [collectionType, releaseTitle]);

  // Reservoir created separate collections for cases where we had multiple songs in the same contract
  // for album editions and artist contracts as part of custom sound market work
  const reservoirCollectionId =
    collectionType === 'Album Edition' || collectionType === 'Artist Contract'
      ? `${contractAddress}:soundxyz-${releaseId}`
      : contractAddress;

  const {
    data: tokens,
    isLoading: isLoadingTokens,
    isFetchingPage,
    fetchNextPage,
    hasNextPage,
    mutate: refetchTokensData,
  } = useTokens(
    {
      collection: reservoirCollectionId,
      limit: 100,
      sortBy: 'floorAskPrice',
      normalizeRoyalties: true,
      ...attributesToFilter,
    },
    {},
    contractChainId,
  );

  const isLoading = marketPlaceEnabled && isLoadingTokens;

  const filteredTokens = useMemo(() => {
    return tokens.filter(
      ({ token, market }) => !!market?.floorAsk?.price && token?.owner !== authUserAddress,
    );
  }, [tokens, authUserAddress]);

  useEffect(() => {
    if (hasNextPage && !isLoadingTokens && quantity === filteredTokens.length) {
      fetchNextPage();
    }
  }, [
    fetchNextPage,
    filteredTokens.length,
    hasNextPage,
    isFetchingPage,
    isLoadingTokens,
    quantity,
  ]);

  const marketPlaceFloorPriceInWei = useMemo(
    () => filteredTokens[0]?.market?.floorAsk?.price?.amount?.raw ?? null,
    [filteredTokens],
  );

  const totalCost = useMemo(() => {
    const tokensToBuy = filteredTokens.slice(0, quantity);
    return tokensToBuy.reduce(
      (acc, token) => {
        const amountInWei = BigInt(token.market?.floorAsk?.price?.amount?.raw ?? 0);
        const amountInUsd = Number(token.market?.floorAsk?.price?.amount?.usd ?? 0);
        return { wei: acc.wei + amountInWei, usd: acc.usd + amountInUsd };
      },
      { wei: BigInt(0), usd: 0 },
    );
  }, [filteredTokens, quantity]);

  const isSufficientFunds = useMemo(() => {
    return totalCost.wei <= (ethBalance?.value ?? 0n);
  }, [ethBalance?.value, totalCost.wei]);

  const marketPlaceFloorPriceInEth =
    marketPlaceEnabled && marketPlaceFloorPriceInWei
      ? formatWeiToEthDisplayFree({ weiValue: marketPlaceFloorPriceInWei, maxDecimals: 4 })
      : null;

  const toAddress = giftTo ?? authUserAddress ?? '';
  const handleMarketPlaceTransaction = useStableCallback(async () => {
    if (currentTxHash) {
      const { success } = await safeHandleTransaction({
        contractMethod: ContractMethod.ARTIST__BUY_EDITION,
        transactionFn: async () => {
          assertIsHex(currentTxHash);
          return currentTxHash;
        },
        chainId: contractChainId,
        openProcessingModal: true,
        showOpenWalletToast: false,
        releaseId,
      });
      if (success) {
        trackClickEvent({
          event: 'Pay Marketplace with ETH Success',
          properties: {
            associationId: releaseId,
            associationType: 'Release',
            quantity,
            chainId: contractChainId,
            contract: contractAddress,
            // In future we should pass in the source component for analytics
            sourceComponent: 'Collect Via Marketplace Modal',
            mintTo: toAddress,
            isGifted: !!giftTo,
          },
        });
      }

      await refetchTokensData().catch(err => {
        logError({
          action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
          error: err,
          message: 'Error when refetching tokens after marketplace buy',
          errorType: ERROR_TYPE.RESERVOIR_MINT,
          indexedTags: {
            releaseId,
            selectedQuantity: quantity,
            chainId: contractChainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
      });

      setCurrentTxHash(null);
      setIsCollecting(false);
    }
  });

  const collectMarketPlace = useTransactionFlow({
    chainId: contractChainId,
    onChainSwitchFail() {
      setIsCollecting(false);
    },
    onAuth: async (_user, { quantity, giftTo }: { quantity: number; giftTo: Address | null }) => {
      setIsCollecting(true);
      if (!client) {
        setIsCollecting(false);
        logError({
          action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
          error: {},
          toast: 'Error while collecting via marketplace',
          message: `Reservoir client not found`,
          errorType: ERROR_TYPE.RESERVOIR_MINT,
          indexedTags: {
            releaseId,
            selectedQuantity: quantity,
            chainId: contractChainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
        return openToast({ text: 'Error when collecting tokens.', variant: 'error' });
      }
      const tokensToBuy = filteredTokens.slice(0, quantity);
      if (tokensToBuy.some(({ token }) => !token || !token?.contract || !token?.tokenId)) {
        setIsCollecting(false);
        return logError({
          action: MINTING_ERROR_ACTIONS.BUY_MARKEPLACE_TOKEN,
          error: { tokensToBuy },
          toast: 'Error while collecting via marketplace',
          message: `Tokens to buy have invalid data`,
          errorType: ERROR_TYPE.RESERVOIR_MINT,
          indexedTags: {
            releaseId,
            selectedQuantity: quantity,
            chainId: contractChainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
      }
      try {
        const signer = await getSigner(contractChainId);
        openToast({
          text: 'Please confirm the transaction in your wallet.',
        });
        logInfo({
          action: MINTING_INFO_ACTIONS.EDITION_MINT,
          message: !!giftTo ? 'Gifting edition from marketplace' : 'Buy from marketplace',
          data: {
            releaseId: releaseId,
            chainId: contractChainId,
            minterAddress: authUserAddress,
            giftingAddress: giftTo,
          },
          pillar: PILLARS.MINTING,
          isArtistOnlyPage: false,
        });
        /**
         * This is critical for a better mobile UX.
         * We have to set before the actual transaction is sent.
         * This way it can open the user's wallet app and make it a seamless flow
         */
        const deepLink = getDeepLink();
        if (deepLink) {
          window.location.href = deepLink;
        }

        const toAddress = !!giftTo ? giftTo : authUserAddress;

        await client.actions.buyToken({
          items: filteredTokens.slice(0, quantity).map(({ token }) => ({
            fillType: 'trade',
            token: `${token?.contract}:${token?.tokenId}`,
          })),

          options: {
            taker: toAddress,
            relayer: authUserAddress,
          },
          wallet: signer,

          onProgress(progress) {
            const saleEventItems = progress.find(step => step.id === 'sale')?.items;
            const txHash =
              saleEventItems && saleEventItems.length > 0 ? saleEventItems[0]?.txHash : null;
            if (!currentTxHash && txHash) {
              setCurrentTxHash(txHash);
            }
          },
          chainId: contractChainId,
        });
      } catch (error) {
        const err = serializeError(error);

        if (err?.message?.includes('taker cannot fill own orders')) {
          openToast({
            text: (
              <>
                At least one edition is already owned by that wallet. Select another edition on
                {SPACE}
                {marketplaceUrl ? (
                  <AccessibleLink
                    absoluteUri={marketplaceUrl}
                    type="external"
                    className="underline"
                  >
                    Sound Market
                  </AccessibleLink>
                ) : (
                  'Sound Market'
                )}
                .
              </>
            ),
            variant: 'error',
          });
        } else {
          openToast({
            text: !!giftTo
              ? `Error when gifting tokens to ${shortenHash(giftTo, 5, 4)}`
              : 'Error when collecting tokens.',
            variant: 'error',
          });
        }

        logError({
          action: MINTING_ERROR_ACTIONS.EDITION_MINT_ERROR,
          error: err,
          message: `Errored while ${!!giftTo ? 'gifting' : 'minting'} via marketplace`,
          errorType: ERROR_TYPE.EDITION_MINT,
          indexedTags: {
            releaseId,
            selectedQuantity: quantity,
            chainId: contractChainId,
          },
          isArtistOnlyPage: false,
          pillar: PILLARS.MINTING,
          level: 'fatal',
        });
        setIsCollecting(false);
      }
    },
  });

  useEffect(() => {
    if (currentTxHash) {
      handleMarketPlaceTransaction();
    }
  }, [currentTxHash, handleMarketPlaceTransaction]);

  const state = useMemo(() => {
    if (!isSufficientFunds) {
      return 'Insufficient funds';
    }
    if (isSwitchingNetwork) {
      return 'Switch chain in wallet';
    }
    if (isCollecting) {
      return 'Confirm in wallet';
    }
    return 'Pay with ETH';
  }, [isCollecting, isSufficientFunds, isSwitchingNetwork]);

  return {
    isLoading,
    marketPlaceFloorPriceInWei,
    collectMarketPlace,
    marketPlaceTokens: filteredTokens,
    isCollecting,
    isSwitchingNetwork,
    state,
    totalCost,
    isSufficientFunds,
    fetchNextPage,
    hasNextPage,
    marketPlaceEnabled,
    marketPlaceFloorPriceInEth,
  };
};
